import { getCookie, networkCode } from "@/utils";
import React, { useEffect, useState } from "react";

import {
  GptConfigType,
  GptType,
  ResponsiveSizeType,
  customTargetingType,
} from "./types";

const exludeCookieFromPages = ["jdPage", "profilePage"];
const DfpSlot: React.FC<
  GptType & {
    isBBSlot?: boolean;
    isFilterSelected?: boolean;
    slotName?: string;
    isLoggedIn?: boolean;
    profileData?: any;
    adPageName?: string;
  }
> = ({
  adUnit,
  name,
  target = [],
  type,
  size,
  isBBSlot = false,
  isLoggedIn = false,
  profileData = null,
  adPageName = "",
}) => {
  let googletag: any;
  let adSlot: any = null;
  const [slotFlag, setSlotFlag] = useState(false);

  const parseValue = (value: string): Array<string> => {
    if (value?.includes(",")) {
      return value
        .split(",")
        .map((val) =>
          decodeURIComponent(val.replace("~", "-")).replace(/(^")|("$)/g, ""),
        );
    } else {
      return [
        decodeURIComponent(value.replace("~", "-")).replace(/(^")|("$)/g, ""),
      ];
    }
  };

  const customTargeting = (): customTargetingType => {
    const params = new URLSearchParams(location.search);
    const queryParams: { [key: string]: string } = {};

    params.forEach((value, key) => {
      queryParams[key] = value;
    });

    const {
      query,
      locations,
      jobCities,
      experienceRanges,
      functions,
      roles,
      industries,
    } = queryParams;

    return {
      key: query ? parseValue(query) : [""],
      loc: locations ? parseValue(locations) : [""],
      exp: experienceRanges ? parseValue(experienceRanges) : [""],
      filterLoc: jobCities ? parseValue(jobCities) : [""],
      filterFunc: functions ? parseValue(functions) : [""],
      filterRoles: roles ? parseValue(roles) : [""],
      filterInd: industries ? parseValue(industries) : [""],
    };
  };

  const displayCommonAd = (targetedKeywords: customTargetingType) => {
    window.googletag.cmd.push(() => {
      window.googletag.pubads().enableAsyncRendering();

      const existingSlot = googletag
        .pubads()
        .getSlots()
        .find((slot: any) => slot.getSlotElementId() === name);

      if (!adSlot && !existingSlot) {
        adSlot = googletag
          .defineSlot(
            `${networkCode.get()}/${adUnit}/${name}`,
            generateSize(),
            name,
          )
          .setTargeting("test", "lazyload")
          .addService(window.googletag.pubads());

        googletag.pubads().enableLazyLoad({
          // Fetch slots within 0.5 viewports.
          fetchMarginPercent: 1,
          // Render slots within 0.2 viewports.
          renderMarginPercent: 1,
          // Double the above values on mobile, where viewports are smaller
          // and users tend to scroll faster.
          mobileScaling: 0.0,
        });

        mappingSize();
        setTargeting(targetedKeywords);
        googletag.enableServices();
        window.googletag.pubads().disableInitialLoad();
        window.googletag.pubads().collapseEmptyDivs(true, true);
        googletag.display(name);

        window.googletag.pubads().refresh([adSlot]);
      } else {
        window.googletag.cmd.push(() => {
          window.googletag.pubads().refresh([existingSlot]);
        });
      }
    });
  };

  const setTargeting = (targetedKeywords: customTargetingType) => {
    window.googletag.pubads().setTargeting("MonsterAPAC-Domain", "rexmonster");
    const domain = window._siteProps_?.country.isoCode ?? "IN";
    window.googletag.pubads().setTargeting("site", domain);
    const { key, loc, exp, filterLoc, filterFunc, filterRoles, filterInd } =
      targetedKeywords;

    if (isBBSlot) {
      window.googletag.pubads().setTargeting("query.key", key);
      window.googletag.pubads().setTargeting("query.loc", loc);
      window.googletag.pubads().setTargeting("query.exp", exp);
      window.googletag.pubads().setTargeting("filter.loc", filterLoc);
      window.googletag.pubads().setTargeting("filter.func", filterFunc);
      window.googletag.pubads().setTargeting("filter.role", filterRoles);
      window.googletag.pubads().setTargeting("filter.Ind", filterInd);
      if (profileData) {
        const {
          profileSkills,
          profileExp,
          profileLoc,
          profileFunc,
          profileInd,
        } = profileData;
        window.googletag.pubads().setTargeting("profile.skills", profileSkills);
        window.googletag.pubads().setTargeting("profile.exp", profileExp);
        window.googletag.pubads().setTargeting("profile.loc", profileLoc);
        window.googletag.pubads().setTargeting("profile.func", profileFunc);
        window.googletag.pubads().setTargeting("profile.ind", profileInd);
      }
    }
  };

  const generateSize = () => {
    const internalSize = size as ResponsiveSizeType;
    if (
      !(
        typeof internalSize === "object" &&
        typeof internalSize[0][1] === "object"
      )
    )
      return size;
    return internalSize.reduce((uniqueSizes: any, elSize: any) => {
      const haveSize = !!uniqueSizes.find(
        (mapSize: [number, number]) => mapSize[0] === elSize[0],
      );
      if (haveSize) uniqueSizes.push(haveSize);
      return uniqueSizes;
    }, []);
  };

  const mappingSize = () => {
    const internalSize = size as ResponsiveSizeType;
    if (
      !(
        typeof internalSize === "object" &&
        typeof internalSize[0][1] === "object"
      )
    )
      return;
    let mapping = googletag.sizeMapping();
    internalSize.forEach((el) => (mapping = mapping.addSize(el)));
    mapping = mapping.build();
    adSlot.defineSizeMapping(mapping);
  };

  useEffect(() => {
    window.googletag = window.googletag || { cmd: [] };
    googletag = window.googletag;

    const brandBoosterCookie = getCookie("brandBoosterAds");
    const isCookieExclusionPage = exludeCookieFromPages.includes(adPageName);

    if (isBBSlot) {
      if (brandBoosterCookie) {
        const brandBoosterAds = JSON.parse(brandBoosterCookie);
        displayCommonAd(
          !brandBoosterAds.shown
            ? customTargeting()
            : brandBoosterAds.targetedKeywords,
        );
      } else {
        displayCommonAd(customTargeting());
      }
    } else {
      displayCommonAd(customTargeting());
    }

    if (
      (window.googletag || typeof window.googletag.pubads === "function") &&
      isBBSlot
    ) {
      window.googletag.cmd.push(() => {
        window.googletag
          .pubads()
          .addEventListener(
            "slotRenderEnded",
            (event: GptConfigType["eventSlotRenderEnded"]) => {
              if (event.slot.getSlotElementId() === name) {
                if (!event.isEmpty) {
                  setSlotFlag(true);
                  if (!brandBoosterCookie) {
                    if (!isCookieExclusionPage) {
                      const expiry = new Date(
                        new Date().getTime() + 1 * 60 * 60 * 1000, // 1 hour
                      ).toUTCString();

                      const cookieValue = JSON.stringify({
                        adShown: !(
                          event.creativeId === 138507841094 ||
                          event.creativeId === 138507840020 ||
                          event.creativeId === 138507840092 ||
                          event.creativeId === 138508460341
                        ),
                        targetedKeywords: customTargeting(),
                      });

                      document.cookie = `brandBoosterAds=${cookieValue};path=/;expires=${expiry}`;
                    }
                  } else {
                    const brandBoosterAds = JSON.parse(brandBoosterCookie);
                    if (!brandBoosterAds.shown) {
                      if (!isCookieExclusionPage) {
                        const expiry = new Date(
                          new Date().getTime() + 1 * 60 * 60 * 1000, // 1 hour
                        ).toUTCString();

                        const cookieValue = JSON.stringify({
                          adShown: !(
                            event.creativeId === 138507841094 ||
                            event.creativeId === 138507840020 ||
                            event.creativeId === 138507840092 ||
                            event.creativeId === 138508460341
                          ),
                          targetedKeywords: customTargeting(),
                        });

                        document.cookie = `brandBoosterAds=${cookieValue};path=/;expires=${expiry}`;
                      }
                    }
                  }
                } else {
                  setSlotFlag(false);
                }
              }
            },
          );
      });
    }
  }, []);

  useEffect(() => {
    if (!slotFlag) {
      setTimeout(() => {
        const adSlot = document.getElementById(name || "");
        if (adSlot?.offsetHeight === 0) {
          switch (name) {
            case "BB-Mobile-SRP-Cards": // 300x250
              adSlot.innerHTML = `<a href="http://appurl.io/foundit"><img src="//media.foundit.in/public/core/images/320x50.webp" alt=${name}></a>`;
              break;
            case "BB-Desktop-JD-Right": // 300x250
              adSlot.innerHTML = `<a href="http://appurl.io/foundit"><img src="//media.foundit.in/public/core/images/300x250JD.webp" alt=${name}></a>`;
              break;
            case "BB-Mobile-JD-Description": // 320x50
            case "BB-Mobile-Profile-Verification": // 320x50
            case "BB-Mobile-Profile-Skills": // 320x50
              adSlot.innerHTML = `<a href="http://appurl.io/foundit"><img src="//media.foundit.in/public/core/images/320x50.webp" alt=${name}></a>`;
              break;
            case "BB-Desktop-SRP-Header": // 728x90
              adSlot.innerHTML = `<a href="http://appurl.io/foundit"><img src="//media.foundit.in/public/core/images/728x90.webp" alt=${name}></a>`;
              break;
            case "BB-Mobile-SRP-Pagination": // 300x250
              // case "BB-Desktop-Profile-Score": // 300x250
              // case "BB-Mobile-Profile-Language": // 300x250
              adSlot.innerHTML = `<a href="http://appurl.io/foundit"><img src="//media.foundit.in/public/core/images/300x250.webp" alt=${name}></a>`;
              break;
          }
          adSlot.style.display = "block";
        }
      }, 3000);
    }
  }, [slotFlag]);

  if (type) return null;

  return <div className="overflow-hidden rounded-2xl" id={name}></div>;
};

export default DfpSlot;
